/**
 * Les annonces à l'écran, et l'ambiance qu'elles commandent.
 *
 * ⚠️ Ce fichier ne s'appelle pas `Annonces.tsx` — il cohabiterait alors avec
 * `annonces.ts` à la seule casse près, ce que macOS accepte et que TypeScript refuse
 * (TS1149). Le nom est donc explicite plutôt que symétrique.
 *
 * Toute la décision est ailleurs — `annonces.ts` dit quoi annoncer, `fil.ts` combien
 * de temps. Ici il ne reste que le branchement React et le rendu.
 *
 * ## Pourquoi un `role="status"` et pas une simple `<div>`
 *
 * Le sec, le rampeau et le passage en décharge changent les règles du tour. Un joueur
 * qui n'a pas l'écran sous les yeux — ou qui ne le voit pas — doit les entendre : ces
 * annonces sont de l'information de jeu, pas de la décoration.
 */

import { useEffect, useRef, useState } from "react";

import type { Partie } from "@zinc421/shared";

import { useTextes } from "../i18n/contexte.js";
import { annoncesEntre, type Annonce, type Ton } from "./annonces.js";
import { ajouter, expirer, filVide, type Fil } from "./fil.js";

/** Secondes écoulées, source unique pour la file. */
function maintenant(): number {
  return performance.now() / 1000;
}

/**
 * Suit une partie et rend ce qu'il faut annoncer.
 *
 * ⚠️ La comparaison se fait sur la **révision**, et non sur l'identité de l'objet :
 * en ligne, l'état est reconstruit à chaque patch, donc toujours « nouveau ». Comparer
 * les références rejouerait toutes les annonces plusieurs fois par seconde.
 */
export function useAnnonces(
  partie: Partie, monSiege?: number,
): readonly Annonce[] {
  const [fil, setFil] = useState<Fil>(filVide);
  const precedent = useRef<Partie | null>(null);
  const derniereRevision = useRef<number>(-1);
  // Le dictionnaire est lu dans une ref : changer de langue ne doit pas rejouer les
  // annonces déjà passées, seulement habiller les suivantes.
  const textes = useTextes();
  const dernierTextes = useRef(textes);
  dernierTextes.current = textes;

  useEffect(() => {
    if (partie.revision === derniereRevision.current) return;
    const avant = precedent.current;
    derniereRevision.current = partie.revision;
    precedent.current = partie;
    if (!avant) return;   // le premier état n'annonce rien : il n'y a pas d'avant

    const neuves = annoncesEntre(avant, partie, dernierTextes.current, monSiege);
    if (neuves.length > 0) setFil((f) => ajouter(f, neuves, maintenant()));
  }, [partie, monSiege]);

  // Le vieillissement : sans lui, une annonce resterait jusqu'au prochain coup.
  useEffect(() => {
    if (fil.visibles.length === 0) return;
    const minuteur = setInterval(() => {
      setFil((f) => expirer(f, maintenant()));
    }, 250);
    return () => { clearInterval(minuteur); };
  }, [fil.visibles.length]);

  return fil.visibles;
}

/** Le ton du moment : celui de la dernière annonce, sinon rien. */
export function tonCourant(annonces: readonly Annonce[]): Ton | null {
  return annonces.at(-1)?.ton ?? null;
}

export function Annonces({ annonces }: { readonly annonces: readonly Annonce[] }) {
  if (annonces.length === 0) return null;
  return (
    <div className="annonces" role="status" aria-live="polite">
      {annonces.map((a) => (
        <p key={a.id} className={`annonce annonce--${a.ton}`}>{a.texte}</p>
      ))}
    </div>
  );
}
